/**
 * KonvaCardPreview — print-accurate live preview of the ID card.
 *
 * Chunk 3 patch (Apr 24 2026): now accepts an optional `layout` prop.
 * When passed, that layout is the source of truth. When omitted, the
 * component falls back to the template preset builders.
 *
 * Caller (IdCardsPage) passes the tenant's saved custom layout via
 * useCardLayout() so the preview matches what downloads render.
 *
 * Perf:
 *   - Layout build memoised on tenant/staff/layout identity
 *   - Content resolution useEffect deps narrowed to primitives that
 *     actually change (URLs, slugs, role, display name)
 */

import { useEffect, useMemo, useState } from 'react';
import { Stage, Layer, Rect, Text as KonvaText } from 'react-konva';
import {
  buildMinimalLayout,
  buildPremiumLayout,
} from '../../lib/cards/build-runtime-layout.js';
import {
  dispatchElementKonva,
  sortByZ,
  renderPhotoPlaceholder,
  renderWaveBandKonva,
  renderMountainsKonva,
  renderBannerImageKonva,
} from '../../lib/cards/konva-renderers.js';
import { CR80, generateQR, verifyUrl, deriveInitials } from '../../lib/cards/shared.js';

const MOCK_STAFF = {
  first_name: 'Dani',
  last_name: 'Morgan',
  role: 'Senior Cleaner',
  photo_url: null,
};

const SCALE = 4;

export function KonvaCardPreview({ tenant = {}, staff = MOCK_STAFF, layout: savedLayout = null }) {
  // When saved layout present: derive procedural helpers from its
  // basedOnTemplate, but use the layout's elements directly.
  const templateForDerived = savedLayout?.basedOnTemplate
    || tenant.card_template
    || 'minimal';

  const { layout, derived } = useMemo(() => {
    const built = templateForDerived === 'premium'
      ? buildPremiumLayout(tenant, staff)
      : buildMinimalLayout(tenant, staff);
    return savedLayout
      ? { layout: savedLayout, derived: built.derived }
      : built;
  }, [
    templateForDerived,
    tenant.brand_color, tenant.card_company_name, tenant.name,
    tenant.card_photo_size, tenant.card_name_font_size,
    tenant.card_footer_text_color, tenant.card_banner_opacity,
    tenant.card_bottom_image_url, tenant.display_name_mode,
    tenant.public_phone, tenant.public_website,
    staff.first_name, staff.last_name, staff.role,
    savedLayout,
  ]);

  const [contentMap, setContentMap] = useState({});

  // Narrow deps — only URLs + text bits that genuinely change content
  const logoUrl = tenant.logo_url;
  const tenantSlug = tenant.slug;
  const bannerUrl = tenant.card_bottom_image_url;
  const photoUrl = staff.photo_url;
  const staffSlug = staff.slug;
  const staffRole = staff.role;
  const displayName = derived.displayName;
  const footerText = derived.footerText;
  const hasFooter = derived.hasFooter;
  const template = templateForDerived;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const next = {};

      if (logoUrl) {
        try {
          const img = await loadImage(logoUrl);
          next.el_logo = { image: img, width: img.naturalWidth, height: img.naturalHeight };
        } catch (e) { /* fallback handled */ }
      }

      if (photoUrl) {
        try {
          const img = await loadImage(photoUrl);
          next.el_staff_photo = { image: img };
        } catch (e) { /* fallback handled */ }
      }

      try {
        const qrUrl = verifyUrl(tenantSlug || 'demo', staffSlug || 'sample');
        const qrDataUrl = await generateQR(qrUrl, 512);
        const qrImg = await loadImage(qrDataUrl);
        next.el_qr = { image: qrImg };
      } catch (e) { /* fallback handled */ }

      if (template === 'premium' && bannerUrl) {
        try {
          const img = await loadImage(bannerUrl);
          next.__banner = { image: img };
        } catch (e) { /* mountain fallback */ }
      }

      next.el_name = { text: displayName };
      if (staffRole) next.el_role = { text: staffRole };
      if (hasFooter && template === 'minimal') {
        next.el_footer_text = { text: footerText };
      }

      if (!cancelled) setContentMap(next);
    })();

    return () => { cancelled = true; };
  }, [logoUrl, tenantSlug, bannerUrl, photoUrl, staffSlug, staffRole,
      template, displayName, footerText, hasFooter]);

  const cardW = CR80.widthMm;
  const cardH = CR80.heightMm;
  const stageW = cardW * SCALE;
  const stageH = cardH * SCALE;

  const ctx = { brandColor: derived.brandColor, tenant, staff };
  const initials = deriveInitials(staff.first_name, staff.last_name);

  // Whether to draw procedural wave/mountain. Applies based on
  // templateForDerived (keeps Premium-based customs with their
  // mountain/banner, Minimal-based customs with their wave).
  const drawWave = template === 'minimal';
  const drawMountain = template === 'premium' && !contentMap.__banner?.image;
  const drawBanner = template === 'premium' && !!contentMap.__banner?.image;

  return (
    <div
      className="card-preview card-preview--konva"
      aria-label={`${template} card preview`}
      style={{
        width: stageW,
        maxWidth: '100%',
        margin: '0 auto',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        background: '#fff',
      }}
    >
      <Stage width={stageW} height={stageH}>
        <Layer listening={false}>
          <Rect x={0} y={0} width={stageW} height={stageH} fill="#FFFFFF" />
          {drawWave && renderWaveBandKonva(
            derived.brandColor, derived.waveBand?.topY || 49, cardW, cardH, SCALE,
          )}
          {drawMountain && renderMountainsKonva(
            derived.brandColor,
            (derived.banner?.topY || 41),
            cardW, cardH, SCALE,
          )}
          {drawBanner && renderBannerImageKonva(
            contentMap.__banner.image,
            derived.bannerOpacity,
            derived.banner,
            derived.brandColor,
            cardW, cardH, SCALE,
          )}

          {sortByZ(layout.elements).map((el) => {
            const node = dispatchElementKonva(el, contentMap[el.id], ctx, SCALE, 'el-');
            if (node) return node;

            // Image fallbacks (no content available)
            if (el.type === 'dynamic_photo') {
              return (
                <PhotoPlaceholderGroup
                  key={el.id}
                  x={el.x} y={el.y} w={el.w} h={el.h}
                  initials={initials}
                />
              );
            }
            if (el.type === 'dynamic_logo' && derived.logoFallback) {
              const f = derived.logoFallback;
              return (
                <FallbackTextNode
                  key={el.id}
                  x={el.x * SCALE}
                  baselineY={(f.y) * SCALE}
                  fontSize={f.fontSize}
                  scale={SCALE}
                  text={f.text}
                  color={f.color}
                  fontWeight={f.fontWeight}
                />
              );
            }
            return null;
          })}

          {/* Premium footer text painted procedurally */}
          {template === 'premium' && hasFooter && (
            <FallbackTextNode
              x={0}
              baselineY={(derived.footerStrip?.textY || 52.8) * SCALE}
              fontSize={derived.footerStrip?.fontSize || 6.5}
              scale={SCALE}
              text={footerText}
              color={resolvePremiumFooterHex(derived, ctx)}
              fontWeight={derived.footerStrip?.fontWeight || 'normal'}
              align="center"
              width={cardW * SCALE}
              centered
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
}

/* ─── Helpers ─── */

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image load failed: ${src}`));
    img.src = src;
  });
}

function FallbackTextNode({ x, baselineY, fontSize, scale, text, color, fontWeight = 'normal', align = 'left', width, centered = false }) {
  const capHPx = fontSize * 0.7 * 0.3528 * scale;
  const y = baselineY - capHPx;
  const fontSizePx = fontSize * 0.3528 * scale;
  const useX = centered ? 0 : x;
  const useWidth = centered ? width : undefined;

  return (
    <KonvaText
      x={useX}
      y={y}
      text={text}
      fontSize={fontSizePx}
      fontFamily="Helvetica, Arial, sans-serif"
      fontStyle={fontWeight === 'bold' ? 'bold' : 'normal'}
      fill={color}
      align={align}
      width={useWidth}
    />
  );
}

function PhotoPlaceholderGroup({ x, y, w, h, initials }) {
  return (
    <>
      <Rect
        x={x * SCALE}
        y={y * SCALE}
        width={w * SCALE}
        height={h * SCALE}
        fill="#F4E9D6"
      />
      <KonvaText
        x={x * SCALE}
        y={y * SCALE}
        width={w * SCALE}
        height={h * SCALE}
        text={initials || '?'}
        fontSize={(w > 25 ? 22 : 18) * 0.3528 * SCALE}
        fontFamily="Helvetica, Arial, sans-serif"
        fontStyle="bold"
        fill="#6B7A70"
        align="center"
        verticalAlign="middle"
      />
    </>
  );
}

function resolvePremiumFooterHex(derived, ctx) {
  const mode = derived.footerMode || 'auto';
  const bg = ctx.brandColor;
  const norm = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * norm(bg.r) + 0.7152 * norm(bg.g) + 0.0722 * norm(bg.b);
  const autoLight = L < 0.5;

  switch (mode) {
    case 'white': return '#FFFFFF';
    case 'dark':  return '#141E1C';
    case 'brand': return autoLight ? '#FFFFFF' : '#141E1C';
    case 'auto':
    default:      return autoLight ? '#FFFFFF' : '#141E1C';
  }
}
